import moment from 'moment-timezone'
import { TIMEZONE_SETTING } from '~/constants/settings'
import { convertTimeZone } from '~/utils/location'

export const USER_DEVICE_ZONE = Object.freeze(moment.tz.guess())

// IANA timezone sent to report endpoints (home, dashboard): the user's manual
// zone when `updateTimeZoneMoment` set a moment default, else the device zone.
// A function, not a constant — the default zone is set after login.
export const getMomentTimezone = () => {
  try {
    return moment().tz() || moment.tz.guess()
  } catch {
    return ''
  }
}

export const mappingTimezone = (tz) => {
  switch (tz) {
    case '-3.5':
      return 'Canada/Newfoundland'
    case '-4':
      return 'Canada/Atlantic'
    case '-5':
      return 'Canada/Eastern'
    case '-6':
      return 'Canada/Central'
    case '-7':
      return 'Canada/Mountain'
    case '-8':
      return 'Canada/Pacific'
    case '-9':
      return 'US/Alaska'
    case '-10':
      return 'US/Aleutian'
    case '+7':
      return 'Asia/Ho_Chi_Minh'
    default:
      return tz
  }
}

export const mappingTimezoneToOffset = (tz) => {
  const timezone = moment().tz(mappingTimezone(tz)).utcOffset() / 60

  return timezone
}

export const convertLocalToGmtTime = (
  time,
  outPutformat = 'YYYY-MM-DDTHH:mm:ss',
  inputFormat = 'YYYY-MM-DDTHH:mm:ss',
  fallback,
) => {
  if (!time) return fallback || ''
  return moment(time, inputFormat).utc().format(outPutformat)
}

export const convertGmtToLocalTime = (
  time,
  outputFormat = 'MMM DD, YYYY hh:mm a',
  inputFormat = 'YYYY-MM-DDTHH:mm:ss',
  fallback,
) => {
  if (!time) return fallback || ''
  return moment(
    `${moment(time, inputFormat).format('YYYY-MM-DDTHH:mm:ss+00:00')}`,
  ).format(outputFormat)
}

// Normalize a warehouse timezone string into a valid moment timezone id.
// Passes IANA names through, maps legacy numeric offsets via mappingTimezone,
// and falls back to 'UTC' when empty/invalid.
export const normalizeWarehouseTimezone = (tz) => {
  if (!tz) return 'UTC'
  const mapped = mappingTimezone(tz)
  return moment.tz.zone(mapped) ? mapped : 'UTC'
}

// Parse a stored UTC value and format it in the given (warehouse) timezone.
// Mirrors convertGmtToLocalTime but targets a named zone instead of device-local.
export const convertGmtToTimezone = (
  time,
  outputFormat = 'MMM DD, YYYY hh:mm a',
  inputFormat = 'YYYY-MM-DDTHH:mm:ss',
  timezone,
  fallback,
) => {
  if (!time) return fallback || ''
  return moment
    .utc(moment(time, inputFormat).format('YYYY-MM-DDTHH:mm:ss'))
    .tz(normalizeWarehouseTimezone(timezone))
    .format(outputFormat)
}

// Interpret a wall-clock string as being in the given (warehouse) timezone and
// output it in UTC. Used when persisting the delivery_date meta (kept in UTC).
export const convertTimezoneToGmt = (
  datetime,
  inputFormat = 'MM/DD/YYYY hh:mm A',
  timezone,
  outputFormat = 'YYYY-MM-DD HH:mm',
) => {
  if (!datetime) return ''
  return moment
    .tz(datetime, inputFormat, normalizeWarehouseTimezone(timezone))
    .utc()
    .format(outputFormat)
}

// User-facing timezone label, e.g. "America/Vancouver (GMT-7)".
export const formatTimezoneLabel = (timezone) => {
  const tz = normalizeWarehouseTimezone(timezone)
  if (tz === 'UTC') return 'Greenwich Mean Time (UTC)'
  return `${tz} (GMT${convertTimeZone(tz)})`
}

// Short "(America/Vancouver)" suffix appended to a displayed date/time so
// it's always clear which timezone the value is shown in.
export const getTimezoneSuffix = (timezone) => {
  const tz = normalizeWarehouseTimezone(timezone)
  return ` (${tz})`
}

export const formatGmtTime = (
  time,
  inputFormat = 'YYYY-MM-DDTHH:mm:ss',
  fallback,
  timezone,
) => {
  if (!time) return fallback || ''
  const parsed = moment(time, inputFormat)
  if (!parsed.isValid()) return fallback || ''

  const isCurrentYear = parsed.year() === moment().year()
  const formatString = isCurrentYear
    ? 'MMM D, [at] h:mm A'
    : 'MMM D, YYYY [at] h:mm A'

  const utcMoment = moment(`${parsed.format('YYYY-MM-DDTHH:mm:ss+00:00')}`)
  if (!timezone) return utcMoment.format(formatString)

  return `${utcMoment.tz(normalizeWarehouseTimezone(timezone)).format(formatString)}${getTimezoneSuffix(timezone)}`
}

export const getDeviceTime = () => moment().tz(USER_DEVICE_ZONE)
export const updateTimeZoneMoment = (tz_setting, tz_timezone) => {
  if (
    tz_setting === TIMEZONE_SETTING.MANUALLY &&
    mappingTimezone(tz_timezone)
  ) {
    moment.tz.setDefault(mappingTimezone(tz_timezone))
  } else {
    moment.tz.setDefault()
  }
}

export const convertToUTC0 = (date = moment().format('YYYY-MM-DD')) => {
  const startDate = moment(date, 'YYYY-MM-DD')
    .startOf('day')
    .utcOffset(0)
    .format('YYYY-MM-DDTHH:mm:ss')
  const endDate = moment(date, 'YYYY-MM-DD')
    .add(1, 'days')
    .startOf('day')
    .utcOffset(0)
    .format('YYYY-MM-DDTHH:mm:ss')

  return [startDate, endDate]
}

// TODO: REMOVE FUNCTIONS
export const getLocalTimeMoment = () => moment()
export const makeTodayMomentWithTz = (timeZone) => {
  if (timeZone) {
    return moment().tz(timeZone)
  }
  return moment()
}
export const convertTimeTzToLocaltime = (timeWithTimezone) =>
  moment(timeWithTimezone)
export const makeMomentWithLocalTz = (datetime, inputFormat) =>
  moment(datetime, inputFormat)

export const formatTimeString = (timeString) =>
  typeof timeString === 'string'
    ? timeString.replace('AM', 'a.m').replace('PM', 'p.m')
    : ''

// This function will work incorrect if local time include timezone
export const convertDateToMomentWithTz = (
  localTime,
  formatLocalTime = 'MM/DD/YYYY',
  timeZone = 'America/Vancouver',
) => {
  const convertTime = moment(localTime, formatLocalTime)
  return moment().tz(timeZone).set({
    year: convertTime.year,
    month: convertTime.month,
    date: convertTime.date,
    hour: 0,
    minute: 0,
    second: 0,
  })
}

// TODO: Re-check again
export const convertTime = (time, format = 'L') => moment(time).format(format)
export const getSimpleDate = (date, format) =>
  date ? moment(date).format(format || 'YYYY-MM-DDTHH:mm:ss') : null
export const getStartOfDay = (date) => moment(date).startOf('day')
export const getEndOfDay = (date) => moment(date).endOf('day')
export const convertH2M = (timeInHour) => {
  if (!timeInHour) return null
  const timeParts = timeInHour.split(':')
  return Number(timeParts[0]) * 60 + Number(timeParts[1])
}
